import {
  ExposedPromise,
  parseWebVTT
} from "./chunk-OU2LSWZ6.js";
import {
  dimensionsToOrientation,
  fetchText
} from "./chunk-CXYZ37JF.js";
import {
  getLogger
} from "./chunk-LFIBM62H.js";

// src/utils/screen-orientation-observer.ts
var ScreenOrientationObserver = class {
  callback;
  observing = false;
  currentEntry;
  boundHandleEvent;
  constructor(callback) {
    this.callback = callback;
    this.boundHandleEvent = this.handleEvent.bind(this);
  }
  observe(options) {
    if (!this.observing) {
      window.screen.orientation?.addEventListener("change", this.boundHandleEvent);
      document.addEventListener("orientationchange", this.boundHandleEvent);
      this.observing = true;
    }
    if (options?.immediateUpdate) {
      if (this.currentEntry) {
        this.callback([this.currentEntry], this);
      } else {
        this.handleEvent();
      }
    }
  }
  disconnect() {
    if (this.observing) {
      window.screen.orientation?.removeEventListener("change", this.boundHandleEvent);
      document.removeEventListener("orientationchange", this.boundHandleEvent);
      this.observing = false;
    }
  }
  handleEvent() {
    let entry = this.getEntryByStandard();
    if (!entry) {
      entry = this.getEntryByLegacy();
    }
    if (entry && (this.currentEntry?.orientation !== entry.orientation || this.currentEntry?.primary !== entry.primary)) {
      this.currentEntry = entry;
      this.callback([entry], this);
    }
  }
  /**
   * Standard screen orientation.
   *
   * Not supported in Safari < 16.4, Edge < 79.
   */
  getEntryByStandard() {
    const type = window.screen.orientation?.type;
    if (type) {
      return {
        orientation: /^portrait/.exec(type) ? "portrait" /* Portrait */ : "landscape" /* Landscape */,
        primary: !/secondary$/.exec(type)
      };
    }
    return void 0;
  }
  /**
   * Fallback screen orientation.
   */
  getEntryByLegacy() {
    const orientation = dimensionsToOrientation(window.innerWidth, window.innerHeight);
    const angle = window.orientation;
    if (orientation && typeof angle === "number") {
      return {
        orientation,
        primary: angle === 0 || angle === 90
      };
    }
  }
};

// src/utils/webvtt-data-file.ts
var WebVTTDataFile = class _WebVTTDataFile {
  cues;
  constructor(cues) {
    this.cues = cues;
  }
  static async load(url, transform) {
    const data = await fetchText(url);
    const webvtt = parseWebVTT(data);
    const cues = webvtt.cues.map((cue) => {
      const c = cue;
      c.data = transform?.(cue);
      return c;
    });
    return new _WebVTTDataFile(cues);
  }
  /**
   * Load a WebVTT file containing URLs and set data.url on each cue object to
   * the the absolute URL (using the WebVTT URL as base).
   */
  static async loadUrlCues(url) {
    return _WebVTTDataFile.load(url, (cue) => ({ url: new URL(cue.text, url).href }));
  }
  /**
   * Search for a single cue on the timestamp given
   */
  search(time) {
    const index = this.searchIndex(time);
    return this.getCue(index);
  }
  /**
   * Search for a list of cues, before and after the timestamp searched for,
   * with the specified interval between cues
   *
   * @param time - timestamp to search for
   * @param interval - interval (seconds) between cues returned
   * @param context - number of cues before and after the cue found on timestamp (if any)
   * @returns A list of found cues or a list of undefined, the length of context * 2 + 1
   */
  searchCues(time, interval, context) {
    const mainCueIndex = this.searchIndex(time);
    const resultLength = context * 2 + 1;
    if (mainCueIndex === void 0) {
      return new Array().fill(void 0, 0, resultLength);
    }
    const mainCueStartTime = this.cues[mainCueIndex].startTime;
    let cues = new Array();
    let increment = 1;
    cues.push(this.getCue(mainCueIndex));
    while (cues.length < resultLength) {
      const before = this.searchIndex(mainCueStartTime - interval * increment);
      const after = this.searchIndex(mainCueStartTime + interval * increment);
      cues = [this.getCue(before), ...cues, this.getCue(after)];
      increment++;
    }
    return cues;
  }
  getCue(index) {
    if (index === void 0) {
      return;
    }
    return this.cues[index] ? this.cues[index] : void 0;
  }
  /**
   * Binary search for the index of the cue matching a given time. The cues are assumed
   * to not overlap.
   */
  searchIndex(time) {
    let low = 0;
    let high = this.cues.length;
    while (low < high) {
      const mid = low + high >>> 1;
      if (this.cues[mid].startTime > time) {
        high = mid;
      } else {
        low = mid + 1;
      }
    }
    const cue = this.cues[low - 1];
    return cue && cue.endTime > time ? low - 1 : void 0;
  }
};

// src/utils/unlock-media-element.ts
var logger = getLogger("UnlockMediaElement");
var unlockedMap = /* @__PURE__ */ new WeakMap();
function unlockMediaElement(element) {
  if (unlockedMap.has(element)) {
    logger.log("Element already unlocked.");
    return;
  }
  if (element.currentSrc) {
    logger.log("Element has source. No need to unlock.");
    return;
  }
  if (element.querySelector("track")) {
    logger.log("Element has tracks but no source. Not touching.");
    return;
  }
  unlockedMap.set(element, true);
  logger.log("Calling load on media element to unlock it.");
  element.load();
}

// src/utils/console-colors.ts
var GOLDEN_RATIO = 1.618033988749895;
function isColorSupported() {
  if (typeof window !== "undefined" && "chrome" in window) {
    return true;
  }
  if (typeof navigator !== "undefined" && /firefox/i.test(navigator.userAgent)) {
    const m = /Firefox\/(\d+\.\d+)/.exec(navigator.userAgent);
    if (m && m[1] && parseFloat(m[1]) >= 31) {
      return true;
    }
  }
  return false;
}
function generateHue(index) {
  return index * GOLDEN_RATIO;
}
function applyColor(messages, style) {
  const css = [
    style.hue !== void 0 ? `color:hsl(${style.hue * 360},99%,40%);` : void 0,
    style.bold ? "font-weight:bold;" : void 0
  ].filter((s) => s !== void 0).join("");
  const startIndex = messages.length - 1;
  for (let i = startIndex; i >= 0; --i) {
    const message = messages[i];
    if (typeof message === "string") {
      messages.splice(i, 1, `%c${message}`, css);
    }
  }
}

// src/utils/console-log-handler.ts
var ConsoleLogHandler = class {
  prefixLength;
  prefixFiller;
  prefixDelimiter;
  color;
  prefixCache = {};
  constructor(options) {
    this.prefixLength = options?.prefixLength ?? 20;
    this.prefixFiller = options?.prefixFiller ?? " ";
    this.prefixDelimiter = options?.prefixDelimiter ?? " |";
    this.color = options?.color ?? isColorSupported();
  }
  log(namespace, level, message, ...messages) {
    if (!(namespace in this.prefixCache)) {
      const prefix = [this.createPrefix(namespace)];
      if (this.color) {
        const hueIndex = Object.keys(this.prefixCache).length;
        applyColor(prefix, { hue: generateHue(hueIndex), bold: true });
      }
      this.prefixCache[namespace] = prefix;
    }
    console[level](...this.prefixCache[namespace], message, ...messages);
  }
  createPrefix(namespace) {
    return `${namespace}${Array(this.prefixLength).join(this.prefixFiller)}`.slice(0, this.prefixLength) + `${this.prefixDelimiter}`;
  }
};

// src/utils/internal-log-handler.ts
var DEFAULT_HISTORY_COUNT = 1e3;
var DEFAULT_CONSOLE_PREFIX_LENGTH = 20;
var InternalLogHandler = class {
  __firstEntry;
  lastEntry;
  numEntries;
  logToConsoleEnabled = false;
  consolePrefixLength;
  namespaceHue;
  historyCount;
  constructor(options) {
    this.historyCount = options?.historyCount ?? DEFAULT_HISTORY_COUNT;
    this.consolePrefixLength = options?.consolePrefixLength ?? DEFAULT_CONSOLE_PREFIX_LENGTH;
    const initEntry = {
      timestamp: Date.now(),
      namespace: "InternalLogHandler",
      level: "log" /* Log */,
      messages: ["Initialized"],
      next: new ExposedPromise()
    };
    this.__firstEntry = initEntry;
    this.lastEntry = initEntry;
    this.numEntries = 1;
    if (isColorSupported()) {
      this.namespaceHue = {};
    }
    if (options?.logToConsole) {
      this.enableLogToConsole();
    }
  }
  get firstEntry() {
    return this.__firstEntry;
  }
  /**
   * Log handler input method.
   */
  log(namespace, level, message, ...messages) {
    this.addLogEntry({
      timestamp: Date.now(),
      namespace,
      level,
      messages: [this.serialize(message)].concat(messages.map((m) => this.serialize(m))),
      next: new ExposedPromise()
    });
  }
  /**
   * Log internal history and all future messages to browser console.
   */
  enableLogToConsole() {
    if (this.logToConsoleEnabled) {
      return;
    }
    this.logToConsoleEnabled = true;
    this.logToConsole().catch((e) => {
      this.logToConsoleEnabled = false;
      console.error(e);
    });
  }
  end() {
    this.lastEntry.next.reject("end");
  }
  async logToConsole() {
    let entry = this.firstEntry;
    let endOfLog = false;
    while (!endOfLog) {
      console[entry.level](
        ...this.toConsolePrefix(entry.namespace),
        ...entry.messages.map((m) => {
          try {
            return JSON.parse(m);
          } catch (_) {
            return m;
          }
        }),
        `(${new Date(entry.timestamp).toLocaleTimeString()})`
      );
      try {
        entry = await entry.next;
      } catch (err) {
        endOfLog = true;
      }
    }
  }
  addLogEntry(newEntry) {
    ++this.numEntries;
    this.lastEntry.next.resolve(newEntry);
    this.lastEntry = newEntry;
    if (this.numEntries > this.historyCount) {
      this.removeFirstLogEntry().catch((e) => {
      });
    }
  }
  async removeFirstLogEntry() {
    try {
      this.__firstEntry = await this.__firstEntry.next;
      --this.numEntries;
    } catch (e) {
    }
  }
  serialize(value) {
    const asString = `${value}`;
    if (asString !== "[object Object]") {
      return asString;
    }
    const seenObjects = [];
    return JSON.stringify(
      value,
      (key, value2) => {
        if (typeof key === "string" && key.charAt(0) === "_") {
          return void 0;
        }
        if (typeof value2 === "object" && value2 !== null) {
          if (seenObjects.includes(value2)) {
            return "...";
          }
          seenObjects.push(value2);
        }
        return value2;
      },
      2
    );
  }
  toConsolePrefix(namespace) {
    const prefix = [
      `${namespace}${Array(this.consolePrefixLength).join(" ")}`.slice(0, this.consolePrefixLength) + "|"
    ];
    if (this.namespaceHue !== void 0) {
      if (!(namespace in this.namespaceHue)) {
        this.namespaceHue[namespace] = generateHue(Object.keys(this.namespaceHue).length);
      }
      applyColor(prefix, { hue: this.namespaceHue[namespace], bold: true });
    }
    return prefix;
  }
};

export {
  ScreenOrientationObserver,
  WebVTTDataFile,
  unlockMediaElement,
  ConsoleLogHandler,
  InternalLogHandler
};
